import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, TableSortLabel, Button, Grid,
  Card, CardContent, Chip, IconButton, Tooltip, InputAdornment,
  TableContainer, TablePagination, CircularProgress, Alert,
  Collapse, useTheme, useMediaQuery, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import deviceApi from '../services/device-api';

function HistoryPage({ cache, setCache }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // State cho filter tạm thời (chỉ gửi khi bấm Filter)
  const [pendingFilters, setPendingFilters] = useState({
    box_id: '',
    msgtype: '',
    sensor_id: '',
    gw_id: '',
  });
  const [filters, setFilters] = useState({
    box_id: '',
    msgtype: '',
    sensor_id: '',
    gw_id: '',
    sort: 'desc',
    page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [events, setEvents] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      const params = {
        ...filters,
        page: filters.page,
        sort: filters.sort,
        limit: rowsPerPage,
      };
      Object.keys(params).forEach(k => {
        if (params[k] === '') delete params[k];
      });
      try {
        const res = await deviceApi.get('/api/events', { params });
        // Backend trả về { events, total_pages, current_page, total }
        setEvents(res.data.events || []);
        setTotalCount(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
        setCurrentPage(res.data.current_page || 1);
      } catch (error) {
        setEvents([]);
        setTotalCount(0);
        setTotalPages(1);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [filters, rowsPerPage]);

  const handleSort = () => {
    setFilters(f => ({ ...f, sort: f.sort === 'desc' ? 'asc' : 'desc' }));
  };

  // Xử lý thay đổi filter tạm thời
  const handlePendingFilterChange = (e) => {
    setPendingFilters(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  // Khi bấm Filter mới gửi filter xuống backend
  const handleApplyFilters = () => {
    setFilters(f => ({
      ...f,
      ...pendingFilters,
      page: 1,
    }));
  };

  const handleClearFilters = () => {
    setPendingFilters({
      box_id: '',
      msgtype: '',
      sensor_id: '',
      gw_id: '',
    });
    setFilters({
      box_id: '',
      msgtype: '',
      sensor_id: '',
      gw_id: '',
      sort: 'desc',
      page: 1,
    });
    setSearch('');
  };

  const handleRefresh = () => {
    setFilters(f => ({ ...f, page: 1 }));
  };

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setFilters(f => ({ ...f, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setFilters(f => ({ ...f, page: 1 }));
  };

  const handleDownload = () => {
    const csvContent = [
      ['ID', 'Timestamp', 'Type', 'Gateway', 'Box ID', 'Sensor ID', 'Payload'],
      ...filteredEvents.map(e => [
        e.id,
        e.timestamp ? new Date(e.timestamp * 1000).toLocaleString() : '',
        e.msgtype,
        e.gw_id,
        e.box_id,
        e.sensor_id,
        JSON.stringify(e)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-history-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Lọc kết quả theo search (client-side)
  const filteredEvents = events.filter(e =>
    JSON.stringify(e).toLowerCase().includes(search.toLowerCase())
  );

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return isMobile ? date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) : date.toLocaleString();
  };

  const getEventTypeColor = (msgtype) => {
    const colors = {
      'error': 'error',
      'warning': 'warning',
      'info': 'info',
      'success': 'success',
      'data': 'primary'
    };
    return colors[msgtype?.toLowerCase()] || 'default';
  };

  const MobileEventCard = ({ event }) => (
    <Card
      sx={{
        mb: 2,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        '&:hover': { boxShadow: 3 }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            ID: {event.id}
          </Typography>
          <Chip
            label={event.msgtype || 'Unknown'}
            color={getEventTypeColor(event.msgtype)}
            size="small"
          />
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Gateway:</strong> {event.gw_id || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Box:</strong> {event.box_id || 'N/A'} | <strong>Sensor:</strong> {event.sensor_id || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Time:</strong> {
              event.received_at
                ? (() => {
                  // Parse chuỗi "YYYY-MM-DD HH:mm:ss.SSS" thành Date (UTC)
                  const utcDate = new Date(event.received_at.replace(' ', 'T') + 'Z');
                  // Cộng thêm 7 giờ (7 * 60 * 60 * 1000 ms)
                  const vnDate = new Date(utcDate.getTime());
                  return vnDate.toLocaleString('en-US', { hour12: false });
                })()
                : 'N/A'
            }
          </Typography>
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <IconButton
            size="small"
            onClick={() => handleViewEvent(event)}
            sx={{ color: 'primary.main' }}
          >
            <ViewIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon />
              Filters & Search
            </Typography>
            <Box display="flex" gap={1}>
              {isMobile && (
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                >
                  {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyFilters}
                startIcon={<FilterIcon />}
                sx={{ mr: 1 }}
              >
                Filter
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  sx={{ color: 'primary.main', bgcolor: 'rgba(0,0,0,0.03)' }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download CSV">
                <span>
                  <IconButton
                    onClick={handleDownload}
                    disabled={filteredEvents.length === 0}
                    sx={{ color: 'primary.main', bgcolor: 'rgba(0,0,0,0.03)' }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Collapse in={showFilters}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Message Type"
                  name="msgtype"
                  value={pendingFilters.msgtype}
                  onChange={handlePendingFilterChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Gateway ID"
                  name="gw_id"
                  value={pendingFilters.gw_id}
                  onChange={handlePendingFilterChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Box ID"
                  name="box_id"
                  value={pendingFilters.box_id}
                  onChange={handlePendingFilterChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Sensor ID"
                  name="sensor_id"
                  value={pendingFilters.sensor_id}
                  onChange={handlePendingFilterChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Search in results"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Stats */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} textAlign="center">
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {filteredEvents.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Filtered Results
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" color="secondary.main" fontWeight="bold">
                {totalCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Events
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {currentPage}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Page
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {totalPages}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Pages
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading events...
          </Typography>
        </Box>
      )}

      {/* No Data State */}
      {!loading && filteredEvents.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No events found matching your criteria. Try adjusting your filters.
        </Alert>
      )}

      {/* Events Display */}
      {!loading && filteredEvents.length > 0 && (
        <>
          {isMobile ? (
            /* Mobile Card View */
            <Box>
              {filteredEvents.map((event, index) => (
                <MobileEventCard key={event.id || index} event={event} />
              ))}
            </Box>
          ) : (
            /* Desktop Table View */
            <Card>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size={isSmallScreen ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active
                          direction={filters.sort}
                          onClick={handleSort}
                        >
                          ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Gateway</TableCell>
                      <TableCell>Box ID</TableCell>
                      <TableCell>Sensor ID</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEvents.map((event, index) => (
                      <TableRow
                        key={event.id || index}
                        sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}
                      >
                        <TableCell>{event.id}</TableCell>
                        {/* <TableCell>{formatTimestamp(event.received_at)}</TableCell> */}
                        <TableCell>
                          {event.received_at
                            ? (() => {
                              // Parse chuỗi "YYYY-MM-DD HH:mm:ss.SSS" thành Date (UTC)
                              const utcDate = new Date(event.received_at.replace(' ', 'T') + 'Z');
                              // Cộng thêm 7 giờ (7 * 60 * 60 * 1000 ms)
                              const vnDate = new Date(utcDate.getTime());
                              return vnDate.toLocaleString('en-US', { hour12: false });
                            })()
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={event.msgtype || 'Unknown'}
                            color={getEventTypeColor(event.msgtype)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{event.gw_id}</TableCell>
                        <TableCell>{event.box_id}</TableCell>
                        <TableCell>{event.sensor_id}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleViewEvent(event)}
                            color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
          {isMobile && filteredEvents.length > 0 && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleChangePage(null, currentPage - 2)}
                disabled={currentPage <= 1}
              >
                Prev
              </Button>
              <Typography variant="body2">
                Page {currentPage} / {totalPages}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleChangePage(null, currentPage)}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </Box>
          )}
          {/* Pagination */}
          <Card sx={{ mt: 2 }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={currentPage - 1}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={isMobile ? "Rows:" : "Rows per page:"}
            />
          </Card>
        </>
      )}

      {/* Event Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Event Details - ID: {selectedEvent?.id}
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <pre style={{
              margin: 0,
              fontSize: isMobile ? '12px' : '14px',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}>
              {JSON.stringify(selectedEvent, null, 2)}
            </pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HistoryPage;